import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Address } from 'viem';
import { Category, categoryLabel, useWeb3, type LockInfo } from '@/hooks/useWeb3';
import { useTokenLock } from '@/hooks/useTokenLock';
import { VestingChart } from '@/components/VestingChart';
import { InvestorUnlockChart } from '@/components/InvestorUnlockChart';
import { Icon, ICONS } from '@/components/brand';
import { Spinner } from '@/components/Spinner';
import { environment } from '@/config/environment';
import { displayedVestedAmount, payableCeiling } from '@/contracts/readState';
import { parseTxError } from '@/utils/txError';

/**
 * The hook is instantiated ONCE, by the screen, and handed down. Calling
 * useTokenLock() again inside a child would start a second, independent poll of
 * every grant on a thirty-second timer.
 */
type TokenLockApi = ReturnType<typeof useTokenLock>;

const explorerForChain = (chainId: number, address: string) => {
  switch (chainId) {
    case 1:
      return `https://etherscan.io/address/${address}`;
    case 11155111:
      return `https://sepolia.etherscan.io/address/${address}`;
    case 8453:
      return `https://basescan.org/address/${address}`;
    case 84532:
      return `https://sepolia.basescan.org/address/${address}`;
    default:
      return `https://blockscan.com/address/${address}`;
  }
};

const formatAddrShort = (a: string) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
const formatDate = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });

/**
 * The allocation id is synthesized from the grant's own contract address, so it
 * is stable, unique per grant, and belongs to the beneficiary rather than to
 * whoever labelled the grant. It stays the hero identifier for exactly that
 * reason: the operator's name for a grant is owner-writable free text.
 */
const allocationIdFor = (lockAddress: string) =>
  lockAddress
    ? `SAHARA-VEST-${lockAddress.slice(2, 6).toUpperCase()}${lockAddress.slice(-4).toUpperCase()}`
    : 'SAHARA-VEST-—';

/**
 * Operator labels for a wallet with several grants follow `{investor}#{round}`
 * so each roster line stays unique. The holder already knows who they are, so
 * the screen keeps only the round. A single grant, or a name with no `#`, is
 * shown in full — that is the whole label.
 */
const beneficiaryGrantLabel = (name: string, grantCount: number): string => {
  if (!name || grantCount <= 1) return name;
  const hash = name.indexOf('#');
  if (hash < 0) return name;
  const round = name.slice(hash + 1);
  return round || name;
};

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
};

// formatTokenAmount already groups the whole part, so this only splits the two halves
// for the hero's large/small type treatment.
const formatTokenWhole = (formatted: string) => {
  const [whole = '0', frac = '000'] = formatted.split('.');
  return { whole, frac };
};

// Every "still locked", "remaining" and percentage figure on this screen is
// computed against payableCeiling (imported from readState, which is also where
// the v7/v9 fallback that makes it necessary lives) rather than against
// totalAmount, which would advertise SAHARA its holder will never receive.

// ---------------------------------------------------------------------------
// One position
// ---------------------------------------------------------------------------

interface PositionViewProps {
  tokenLock: TokenLockApi;
  lock: LockInfo;
  lockAddress: string;
  /** The operator's label for this grant. Empty on a v7/v9 factory or when redacted. */
  name: string;
  /** Rendered above the header when this wallet holds more than one grant. */
  switcher?: React.ReactNode;
}

const PositionView: React.FC<PositionViewProps> = ({
  tokenLock,
  lock,
  lockAddress,
  name,
  switcher,
}) => {
  const web3 = useWeb3();
  const now = useNow();
  const isInvestor = lock.category === Category.INVESTOR;
  const tgeSec = Number(tokenLock.tgeTime ?? 0n);
  const tgeLabel = tgeSec > 0 ? `TGE ${formatDate(new Date(tgeSec * 1000))}` : 'TGE not set';

  const cancelled = lock.cancelled;
  const offboardSec = lock.offboardTime ? Number(lock.offboardTime) : 0;
  const isOffboarded = offboardSec > 0;
  const offboardDate = isOffboarded ? new Date(offboardSec * 1000) : null;
  const offboardEffective = isOffboarded && offboardSec * 1000 <= now;

  // The honest ceiling. Everything below divides by this, never by totalAmount.
  const entitlement = useMemo(() => payableCeiling(lock, now), [lock, now]);
  const ceilingDropped = entitlement < lock.totalAmount;
  // What of the allocation will never reach this wallet. Measured against the
  // ceiling OR what has already been paid out, whichever is larger: on a
  // cancelled grant the ceiling is zero but the tokens already claimed were
  // still received, so `totalAmount - ceiling` alone would count them as lost.
  const neverPayable = entitlement > lock.unlockedAmount ? entitlement : lock.unlockedAmount;
  const forfeited = lock.totalAmount > neverPayable ? lock.totalAmount - neverPayable : BigInt(0);

  // A grant can now be resized downward at any point in its life, and the
  // schedule it rescales includes the past — so `already claimed` can legally
  // exceed both what the schedule has released to date and the grant's whole
  // total. Neither of the two figures below may ever go negative, drive a
  // percentage past 100 or reverse a bar, so both are clamped at the source.
  //
  // Released by the schedule as of today: entitledAmount() on chain, which is
  // the lower of the two curves. Both are reported raw by getLockState.
  const releasedToDate =
    lock.vestedAmount < lock.unlockScheduleAmount ? lock.vestedAmount : lock.unlockScheduleAmount;
  // Already received MORE than this schedule has released so far. Claims pause
  // until the curve climbs past it; nothing is lost and nothing is at risk.
  const aheadOfSchedule =
    !cancelled && lock.unlockedAmount > releasedToDate
      ? lock.unlockedAmount - releasedToDate
      : BigInt(0);
  // Already received more than the WHOLE grant is now worth, which is the one
  // case where the pause never ends. Excluded on a cancelled grant, where the
  // ceiling is zero for an entirely different reason and its own banner says so.
  const overpaid =
    !cancelled && lock.unlockedAmount > entitlement
      ? lock.unlockedAmount - entitlement
      : BigInt(0);

  const grantedTokens = useMemo(
    () => web3.formatTokenAmount(lock.totalAmount),
    [lock.totalAmount, web3.formatTokenAmount],
  );
  const entitlementTokens = useMemo(
    () => web3.formatTokenAmount(entitlement),
    [entitlement, web3.formatTokenAmount],
  );
  const forfeitedTokens = useMemo(
    () => web3.formatTokenAmount(forfeited),
    [forfeited, web3.formatTokenAmount],
  );
  const aheadOfScheduleTokens = useMemo(
    () => web3.formatTokenAmount(aheadOfSchedule),
    [aheadOfSchedule, web3.formatTokenAmount],
  );
  const releasedToDateTokens = useMemo(
    () => web3.formatTokenAmount(releasedToDate),
    [releasedToDate, web3.formatTokenAmount],
  );
  // An investor's cliff IS the unlock cliff, so their vested figure is the
  // unlock figure; see displayedVestedAmount.
  const vested = displayedVestedAmount(lock);
  const vestedTokens = useMemo(
    () => web3.formatTokenAmount(vested),
    [vested, web3.formatTokenAmount],
  );
  const claimedTokens = useMemo(
    () => web3.formatTokenAmount(lock.unlockedAmount),
    [lock.unlockedAmount, web3.formatTokenAmount],
  );
  // availableAmount is claimableAmount() on chain: already the lower of what has
  // been earned and what the allocation contract actually holds. It is the only
  // number this screen may present as claimable.
  const availableTokens = useMemo(
    () => web3.formatTokenAmount(lock.availableAmount),
    [lock.availableAmount, web3.formatTokenAmount],
  );
  // Earned by the schedule and not yet claimed, whether or not the tokens have
  // been transferred in yet. This is the "we owe you this" number.
  const earnedTokens = useMemo(
    () => web3.formatTokenAmount(lock.earnedUnclaimed),
    [lock.earnedUnclaimed, web3.formatTokenAmount],
  );
  // The gap between the two: earned, still owed, not yet transferred in. Tokens
  // arrive in instalments, so this is an ordinary state, not an error.
  const awaitingFunding = useMemo(
    () =>
      lock.earnedUnclaimed > lock.availableAmount
        ? lock.earnedUnclaimed - lock.availableAmount
        : BigInt(0),
    [lock.earnedUnclaimed, lock.availableAmount],
  );
  const awaitingFundingTokens = useMemo(
    () => web3.formatTokenAmount(awaitingFunding),
    [awaitingFunding, web3.formatTokenAmount],
  );

  // Still locked: what the grant can still pay, less what has been claimed and
  // less what has been earned but not yet collected — earnedUnclaimed rather
  // than the claimable figure, so a tranche that is earned but not yet
  // transferred in is not double-counted as still locked.
  //
  // Against the CEILING, not the allocation. Against totalAmount a cancelled
  // grant would report almost its whole allocation as "still locked" when in
  // fact every token of it has been forfeited.
  const remainingTokens = useMemo(() => {
    const claimedAndEarned = lock.unlockedAmount + lock.earnedUnclaimed;
    const value = entitlement > claimedAndEarned ? entitlement - claimedAndEarned : BigInt(0);
    return web3.formatTokenAmount(value);
  }, [entitlement, lock.unlockedAmount, lock.earnedUnclaimed, web3.formatTokenAmount]);

  // Clamped to 0–100 at the source, not at each call site. A grant resized below
  // what has already been claimed makes `claimed / entitlement` exceed one, and
  // every consumer of this — two summary tiles, the hero line and the chart's
  // today marker — would otherwise print a percentage above 100 or draw a point
  // off the top of the plot. The overpaid case is stated in words instead.
  const percentOfEntitlement = (value: bigint) => {
    if (entitlement <= BigInt(0) || value <= BigInt(0)) return 0;
    const raw = Number((value * BigInt(100000)) / entitlement) / 1000;
    return Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
  };

  const vestingProgress = useMemo(
    () => percentOfEntitlement(vested),
    [vested, entitlement],
  );
  const claimedProgress = useMemo(
    () => percentOfEntitlement(lock.unlockedAmount),
    [lock.unlockedAmount, entitlement],
  );
  const unlockedProgress = useMemo(() => {
    // unlockScheduleAmount is the contract's own calculateUnlockedAmount, so this
    // follows block.timestamp rather than the browser clock. v10 already clamps it
    // at the ceiling; the clamp is repeated here because a v9 factory does not.
    const unlocked =
      lock.unlockScheduleAmount > entitlement ? entitlement : lock.unlockScheduleAmount;
    return percentOfEntitlement(unlocked);
  }, [lock.unlockScheduleAmount, entitlement]);

  const schedule = useMemo(
    () => tokenLock.calculateVestingSchedule(lock),
    [tokenLock, lock],
  );
  // The next milestone that would actually PAY something. A downward resize
  // rescales the past, so a milestone whose date is still ahead can already be
  // covered by what has been claimed; counting down to one of those would
  // promise a payment that is never coming.
  const nextUnlock = useMemo(
    () => schedule.find((s) => !s.unlocked && !s.settled),
    [schedule],
  );

  // This grant's unlock track. The track only names which fixed cliff date
  // applies; the date itself is owner-set on the factory and read from chain
  // below, so it is never derived from the vesting constants.
  const track = useMemo(() => ({ label: categoryLabel(lock.category) }), [lock.category]);

  const startDate = useMemo(() => new Date(Number(lock.startTime) * 1000), [lock.startTime]);

  // The fixed date this track releases its 25%, straight from the factory. 0 while
  // the owner has not set it, which is the same sentinel the contract gates on.
  const unlockCliffSec = Number(lock.unlockCliffTime);
  const cliffDate = useMemo(
    () => (unlockCliffSec ? new Date(unlockCliffSec * 1000) : null),
    [unlockCliffSec],
  );

  // The unlock curve ends with its last monthly tranche, at cliff + 36 x 30 days.
  // Only the cliff is a calendar date; the tranches after it are 30-day steps, so
  // this is deliberately not three calendar years past the cliff.
  const endDate = useMemo(() => {
    if (!unlockCliffSec || !tokenLock.durations) return null;
    const monthlySec = Number(tokenLock.durations.monthlyDuration);
    const tranches = Number(tokenLock.durations.trancheCount);
    if (monthlySec <= 0 || tranches <= 0) return null;
    return new Date((unlockCliffSec + tranches * monthlySec) * 1000);
  }, [unlockCliffSec, tokenLock.durations]);

  const chartMeta = useMemo(() => {
    if (!tokenLock.durations) return null;
    const monthlySec = Number(tokenLock.durations.monthlyDuration);
    const cliffSec = Number(tokenLock.durations.cliffDuration);
    const vestingSec = Number(tokenLock.durations.vestingDuration);
    if (monthlySec <= 0 || vestingSec <= 0) return null;
    const totalMonths = Math.max(1, Math.floor(vestingSec / monthlySec));
    const cliffMonths = Math.max(0, Math.floor(cliffSec / monthlySec));
    const unlockTranches = Number(tokenLock.durations.trancheCount);
    const startSec = Number(lock.startTime);
    const nowSec = Math.floor(now / 1000);
    const elapsedSec = Math.max(0, nowSec - startSec);
    const currentMonth = Math.min(totalMonths, elapsedSec / monthlySec);
    const offboardMonth = isOffboarded
      ? Math.min(totalMonths, Math.max(0, (offboardSec - startSec) / monthlySec))
      : undefined;
    // The chart's month-space puts startTime at the origin and draws the vesting
    // cliff at cliffMonths; the unlock curve is that same shape shifted by
    // unlockOffsetMonths. The unlock cliff's real position is
    // (unlockCliffSec - startSec) / monthlySec, so the shift it needs is that
    // position less cliffMonths. This is deliberately NOT floored at 0: the
    // unlock cliff is a fixed date, so someone onboarded within a year of it
    // reaches it before their vesting cliff and needs a negative shift. The
    // chart pads whichever curve starts later.
    const unlockOffsetMonths =
      unlockCliffSec > 0 ? (unlockCliffSec - startSec) / monthlySec - cliffMonths : 0;
    return { totalMonths, cliffMonths, unlockTranches, currentMonth, offboardMonth, unlockOffsetMonths };
  }, [tokenLock.durations, lock.startTime, now, isOffboarded, offboardSec, unlockCliffSec]);

  const countdown = useMemo(() => {
    if (!nextUnlock || nextUnlock.forfeited) return null;
    const diff = Math.max(0, nextUnlock.date.getTime() - now);
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    return { days, hours, minutes };
  }, [nextUnlock, now]);

  // countdown goes null both when the schedule is finished and when there is no
  // schedule to show yet (no cliff date set, or durations still loading), so name
  // those apart instead of telling an unscheduled beneficiary they are fully vested.
  const nextUnlockFallback = cancelled
    ? 'Cancelled'
    : // An allocation already paid past its own total has a schedule that keeps
      // running and a payout that never resumes. Counting down to a tranche that
      // can never pay would be the one misleading thing on this screen.
      overpaid > BigInt(0)
      ? 'Nothing further'
      : offboardEffective
        ? 'Vesting frozen'
        : !unlockCliffSec
          ? 'Not scheduled'
          : schedule.length === 0
            ? '—'
            : 'Fully vested';

  const canClaim = Boolean(lock.isInitialized && lock.availableAmount > BigInt(0));

  const handleClaim = async () => {
    try {
      // The grant is named explicitly: with several positions per wallet there is
      // no "the" lock, and guessing one would claim from the wrong position.
      await tokenLock.claimTokens(lockAddress as Address);
      // claimTokens resolves only after the receipt is confirmed and balances are
      // re-read, so by this point the tokens are already in the wallet.
      toast.success('Claim confirmed. The tokens are now in your wallet.');
    } catch (e: any) {
      const parsed = parseTxError(e, 'Claim failed.');
      if (parsed.kind === 'rejected') {
        toast.info('Claim cancelled in wallet.');
      } else {
        toast.error(parsed.message);
      }
    }
  };

  const beneficiary = lock.beneficiary ?? '';
  const factory = environment.factoryAddress;
  const allocationId = allocationIdFor(lockAddress);

  const available = formatTokenWhole(availableTokens);

  // The summary strip. The fifth tile only appears when something has actually
  // been forfeited, so an ordinary grant reads exactly as it did before.
  const summaryTiles = [
    {
      label: ceilingDropped ? 'Payable allocation' : 'Total allocation',
      value: entitlementTokens,
      sub: cancelled
        ? `Cancelled · ${grantedTokens} granted`
        : offboardEffective && offboardDate
          ? `Frozen ${formatDate(offboardDate)} · ${grantedTokens} granted`
          : isInvestor ? tgeLabel : `Vesting start ${formatDate(startDate)}`,
    },
    {
      label: 'Vested to date',
      value: vestedTokens,
      sub: `${vestingProgress.toFixed(2)}% of ${ceilingDropped ? 'what remains payable' : 'allocation'}`,
    },
    {
      label: 'Claimed to date',
      value: claimedTokens,
      // Never a percentage when it would read above 100: a grant revised below
      // what its holder has already received is stated in tokens instead.
      sub:
        overpaid > BigInt(0)
          ? `More than the ${entitlementTokens} this allocation now totals`
          : `${claimedProgress.toFixed(2)}% of ${ceilingDropped ? 'what remains payable' : 'allocation'}`,
    },
    {
      label: 'Remaining locked',
      value: remainingTokens,
      sub: cancelled
        ? 'Nothing remains'
        : overpaid > BigInt(0)
          ? 'Nothing further will be released'
          : endDate
            ? `Schedule end ${formatDate(endDate)}`
            : '—',
    },
    ...(forfeited > BigInt(0)
      ? [
          {
            label: 'Forfeited',
            value: forfeitedTokens,
            sub: cancelled
              ? 'Cancelled — including SAHARA already earned'
              : offboardDate
                ? `Unvested at ${formatDate(offboardDate)}`
                : 'Not payable',
          },
        ]
      : []),
  ];

  return (
    <div className="sh-main">
      <div className="sh-main-inner">
        {switcher}

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40, gap: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {/* The allocation id stays the hero label. It is derived from this
                  grant's own contract address, so it cannot be edited by anyone. */}
              <div className="sh-label">Allocation · {allocationId}</div>
              <span className="sh-pill">{track.label} track</span>
              {cancelled && <span className="sh-pill sh-pill-danger">Cancelled</span>}
              {isOffboarded && offboardDate && !cancelled && (
                <span className="sh-pill sh-pill-danger">
                  {offboardEffective
                    ? `Vesting frozen ${formatDate(offboardDate)}`
                    : `Vesting freezes ${formatDate(offboardDate)}`}
                </span>
              )}
            </div>
            <h1 className="sh-h1">
              Your{' '}
              <span className="sh-serif" style={{ fontStyle: 'italic', color: 'var(--text-2)' }}>
                SAHARA
              </span>{' '}
              allocation
            </h1>
            {/* The operator's label, subordinate to the id above and visibly
                attributed. It is owner-writable free text on the factory — the
                only chain-sourced string this screen has ever shown — so it is
                presented as somebody's note about the grant, never as the
                grant's identity. */}
            {name && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  marginTop: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span className="sh-cause">Operator label</span>
                <span style={{ fontSize: 14, color: 'var(--text-2)' }}>“{name}”</span>
                <span className="sh-cause" style={{ color: 'var(--text-muted)' }}>
                  set by Sahara operations · not part of your entitlement
                </span>
              </div>
            )}
            <p className="sh-sub" style={{ marginTop: 14 }}>
              This is the non-transferable allocation tied to this wallet.{' '}
              {isInvestor ? tgeLabel : `Vesting start ${formatDate(startDate)}`}.
              {cancelled ? (
                <>
                  {' '}
                  This grant has been cancelled. Nothing further will vest, unlock or become
                  claimable, and the SAHARA it had not yet paid out has been returned.
                </>
              ) : offboardEffective && offboardDate ? (
                <>
                  {' '}
                  Vesting was frozen on {formatDate(offboardDate)}. SAHARA vested through that date
                  remains subject to the token release schedule and can be claimed once available.
                </>
              ) : (
                cliffDate &&
                endDate && (
                  <>
                    {' '}
                    Unlock cliff {now / 1000 >= unlockCliffSec ? 'cleared' : 'lands'}{' '}
                    {formatDate(cliffDate)} on the {track.label} track. Monthly unlock continues
                    through {formatDate(endDate)}.
                  </>
                )
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {lockAddress && (
              <a
                className="sh-btn"
                href={explorerForChain(environment.chainId, lockAddress)}
                target="_blank"
                rel="noreferrer"
              >
                <Icon d={ICONS.external} size={14} />
                View allocation contract
              </a>
            )}
          </div>
        </div>

        {/* Cancellation is stated first and without hedging: it is the one state
            on this screen that ends the allocation rather than pausing it. */}
        {cancelled && (
          <div className="sh-banner sh-banner-danger" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                This allocation has been cancelled
              </div>
              <div style={{ marginTop: 2 }}>
                {forfeitedTokens} SAHARA has been forfeited — everything that had not already been
                claimed, including SAHARA that had vested and been earned but not yet collected.
                Nothing further will vest or unlock, and no amount of this allocation is or will
                become claimable.
                {lock.unlockedAmount > BigInt(0) &&
                  ` The ${claimedTokens} SAHARA already claimed is unaffected and stays in your wallet.`}{' '}
                If you believe this is an error, contact Sahara AI support — it cannot be undone
                on chain, only replaced with a new grant.
              </div>
            </div>
          </div>
        )}

        {/* Already received more than the schedule has released to date, which
            only a downward revision of the allocation can produce. Stated plainly
            and without alarm: the SAHARA already claimed is theirs, is in their
            wallet and is not at risk — the only thing that changes is that the
            schedule has nothing further to pay out yet, or at all. */}
        {aheadOfSchedule > BigInt(0) && (
          <div className="sh-banner sh-banner-warn" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.info} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                {overpaid > BigInt(0)
                  ? `This allocation has been revised to ${entitlementTokens} SAHARA`
                  : 'You are ahead of this allocation’s schedule'}
              </div>
              <div style={{ marginTop: 2 }}>
                {overpaid > BigInt(0) ? (
                  <>
                    You have already received {claimedTokens} SAHARA from it, which is more than the
                    revised total. Those tokens are yours: they are in your wallet, they are not
                    affected by the revision and nothing about this page can take them back. What it
                    does mean is that this allocation has nothing further to pay — no additional
                    SAHARA will become claimable from it. If that is not what you expected, contact
                    Sahara AI support.
                  </>
                ) : (
                  <>
                    You have already received {claimedTokens} SAHARA, and this allocation’s schedule
                    has released {releasedToDateTokens} to date — you are {aheadOfScheduleTokens}{' '}
                    ahead of it. Everything you have claimed is yours and stays in your wallet.
                    Further claims open again once the schedule passes what you have already
                    received.
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Earned but not yet transferred in. SAHARA arrives in instalments rather
            than as one upfront deposit, so this is a normal state and the number
            stays owed either way — but it must never be shown as claimable. */}
        {awaitingFunding > BigInt(0) && (
          <div className="sh-banner sh-banner-warn" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.info} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                {awaitingFundingTokens} SAHARA earned, awaiting transfer
              </div>
              <div style={{ marginTop: 2 }}>
                {tokenLock.incrementalFunding === false
                  ? `You have earned ${earnedTokens} SAHARA under the schedule. The treasury must fund the full earned balance before this allocation can pay a claim.`
                  : `You have earned ${earnedTokens} SAHARA under the schedule. ${availableTokens} of that is in your allocation contract and claimable now; the rest awaits a treasury top-up. It stays owed to you in the meantime.`}
              </div>
            </div>
          </div>
        )}

        {!cancelled && !lock.isInitialized && awaitingFunding === BigInt(0) && (
          <div className="sh-banner sh-banner-warn" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                Allocation not yet activated
              </div>
              <div style={{ marginTop: 2 }}>
                Your allocation contract is deployed. The treasury must activate it before you
                can claim tokens released by the schedule.
              </div>
            </div>
          </div>
        )}

        {!cancelled && !unlockCliffSec && (
          <div className="sh-banner sh-banner-warn" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                Token release schedule not set
              </div>
              <div style={{ marginTop: 2 }}>
                Your individual vesting continues to accrue. SAHARA cannot become claimable until
                the token release schedule is activated. The unlock cliff for the {track.label}{' '}
                track is a fixed date; it appears here once it is set.
              </div>
            </div>
          </div>
        )}

        {offboardEffective && offboardDate && !cancelled && (
          <div className="sh-banner sh-banner-danger" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.info} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                Vesting frozen as of {formatDate(offboardDate)}
              </div>
              <div style={{ marginTop: 2 }}>
                You can still claim SAHARA that vested before this date once it is available under
                the token release schedule. No additional SAHARA will vest while the schedule
                remains frozen.
              </div>
            </div>
          </div>
        )}

        {tokenLock.error && (
          <div className="sh-banner sh-banner-danger" style={{ marginBottom: 24 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div style={{ flex: 1 }}>{tokenLock.error}</div>
            <button
              type="button"
              onClick={() => tokenLock.setError(null)}
              className="sh-btn sh-btn-ghost sh-btn-sm"
              aria-label="Dismiss"
              style={{ padding: 6 }}
            >
              <Icon d={ICONS.close} size={14} />
            </button>
          </div>
        )}

        {/* Hero claim + next unlock */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 1,
            background: 'var(--border)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
            marginBottom: 32,
          }}
        >
          <div style={{ background: 'var(--surface-1)', padding: '32px 36px' }}>
            {/* claimableAmount() from the contract: what claim() pays this moment,
                never the larger earned figure. It is 0 on a cancelled grant. */}
            <div className="sh-label">Claimable now</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 18, flexWrap: 'wrap' }}>
              <span className="sh-hero-num" style={{ color: canClaim ? 'var(--accent)' : 'var(--text)' }}>
                {available.whole}
              </span>
              <span className="sh-num" style={{ fontSize: 36, color: 'var(--text-muted)', letterSpacing: '-0.04em' }}>
                .{available.frac}
              </span>
              <span className="sh-hero-unit">SAHARA</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, fontSize: 13, color: 'var(--text-2)', flexWrap: 'wrap' }}>
              <span>
                {claimedTokens} <span style={{ color: 'var(--text-muted)' }}>SAHARA claimed to date</span>
              </span>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span style={{ color: 'var(--success)' }}>
                {vestingProgress.toFixed(2)}% vested
              </span>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              {/* Blue matches the unlock curve in the chart legend. */}
              <span style={{ color: 'var(--info)' }}>
                {unlockedProgress.toFixed(2)}% unlocked
              </span>
            </div>
            {awaitingFunding > BigInt(0) && (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-2)' }}>
                {earnedTokens} <span style={{ color: 'var(--text-muted)' }}>SAHARA earned</span> ·{' '}
                <span style={{ color: 'var(--accent)' }}>
                  {awaitingFundingTokens} awaiting transfer
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 28, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleClaim}
                disabled={!canClaim || tokenLock.isLoading}
                className="sh-btn sh-btn-primary sh-btn-lg"
                title={
                  cancelled
                    ? 'This allocation has been cancelled'
                    : !lock.isInitialized
                      ? 'Allocation not yet activated'
                      : awaitingFunding > BigInt(0) && !canClaim
                        ? 'Your earned SAHARA has not been transferred into the allocation contract yet'
                        : undefined
                }
              >
                {tokenLock.isClaiming ? 'Claiming…' : 'Claim'}
                <Icon d={ICONS.arrowRight} size={14} />
              </button>
            </div>
            <div className="sh-cause" style={{ marginTop: 20 }}>
              {cancelled
                ? 'This allocation was cancelled — nothing is or will become claimable'
                : canClaim
                  ? awaitingFunding > BigInt(0)
                    ? `Claims ${availableTokens} SAHARA now · the remaining ${awaitingFundingTokens} follows with the next transfer · onchain transaction, network gas fee applies`
                    : 'Onchain transaction · network gas fee applies'
                  : awaitingFunding > BigInt(0)
                    ? 'Claiming opens as soon as the earned SAHARA above reaches your allocation contract'
                    : overpaid > BigInt(0)
                      ? 'You have already received more than this allocation now totals — nothing further will become claimable from it'
                      : aheadOfSchedule > BigInt(0)
                        ? `You are ${aheadOfScheduleTokens} SAHARA ahead of this schedule — claims reopen once it passes what you have already received`
                        : lock.isInitialized
                          ? 'Nothing is claimable yet — the next unlock is shown alongside'
                          : 'Claims open once your allocation is activated and the schedule releases a tranche'}
            </div>
          </div>

          <div
            style={{
              background: 'var(--surface-1)',
              padding: '32px 36px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div>
              <div className="sh-label">Next unlock</div>
              {countdown && overpaid === BigInt(0) ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 0, marginTop: 18, flexWrap: 'wrap' }}>
                  <span className="sh-num" style={{ fontSize: 44, fontWeight: 300, letterSpacing: '-0.04em' }}>
                    {countdown.days}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 2 }}>d</span>
                  <span className="sh-num" style={{ fontSize: 44, fontWeight: 300, letterSpacing: '-0.04em', marginLeft: 16 }}>
                    {countdown.hours}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 2 }}>h</span>
                  <span className="sh-num" style={{ fontSize: 44, fontWeight: 300, letterSpacing: '-0.04em', marginLeft: 16 }}>
                    {countdown.minutes}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 2 }}>m</span>
                </div>
              ) : (
                <div style={{ marginTop: 18 }}>
                  <span className="sh-num" style={{ fontSize: 36, fontWeight: 300, letterSpacing: '-0.04em' }}>
                    {nextUnlockFallback}
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div className="sh-label">Amount</div>
                <div className="sh-num" style={{ fontSize: 16, marginTop: 6 }}>
                  {nextUnlock && !cancelled && overpaid === BigInt(0)
                    ? web3.formatTokenAmount(nextUnlock.amount)
                    : '0'}{' '}
                  <span className="sh-mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>SAHARA</span>
                </div>
              </div>
              <div>
                <div className="sh-label">Date</div>
                <div style={{ fontSize: 14, marginTop: 6 }}>
                  {nextUnlock && !cancelled && overpaid === BigInt(0)
                    ? formatDate(nextUnlock.date)
                    : '—'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        {chartMeta && (
          <div className="sh-card" style={{ marginBottom: 24, padding: '28px 32px' }}>
            <div className="sh-card-head" style={{ alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div className="sh-label">Unlock curve</div>
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-2)' }}>
                  {entitlementTokens} SAHARA{isInvestor ? ` · ${tgeLabel}` : ''}{cliffDate ? ` · unlock cliff ${formatDate(cliffDate)}` : ''} · {chartMeta.unlockTranches}-month linear unlock
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 18,
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.05em',
                }}
              >
                {!isInvestor && <>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 2, background: 'var(--accent)' }} />VESTING SCHEDULE
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      width: 10,
                      height: 8,
                      background: 'var(--accent-soft)',
                      border: '1px solid var(--accent-dim)',
                    }}
                  />
                  VESTED
                </span>
                </>}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 2, background: 'var(--info)' }} />UNLOCK SCHEDULE
                </span>
              </div>
            </div>
            <div style={{ height: 260 }}>
              {isInvestor ? (
                <InvestorUnlockChart
                  tgeTime={tgeSec}
                  cliffTime={unlockCliffSec}
                  monthlyDuration={Number(tokenLock.durations!.monthlyDuration)}
                  trancheCount={chartMeta.unlockTranches}
                  now={now / 1000}
                  unlockedPercent={unlockedProgress}
                />
              ) : <VestingChart
                totalMonths={chartMeta.totalMonths}
                cliffMonths={chartMeta.cliffMonths}
                currentMonth={chartMeta.currentMonth}
                vestedPercent={vestingProgress}
                offboardMonth={chartMeta.offboardMonth}
                unlockOffsetMonths={chartMeta.unlockOffsetMonths}
              />}
            </div>
          </div>
        )}

        {/* Allocation summary */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${summaryTiles.length}, 1fr)`,
            gap: 0,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
            background: 'var(--surface-1)',
          }}
        >
          {summaryTiles.map((row, i) => (
            <div
              key={row.label}
              style={{
                padding: '22px 24px',
                borderLeft: i === 0 ? 0 : '1px solid var(--border)',
              }}
            >
              <div className="sh-label">{row.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 12 }}>
                <span
                  className="sh-num"
                  style={{
                    fontSize: 22,
                    fontWeight: 400,
                    letterSpacing: '-0.03em',
                    color: row.label === 'Forfeited' ? 'var(--danger)' : undefined,
                  }}
                >
                  {row.value}
                </span>
                <span className="sh-mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>SAHARA</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{row.sub}</div>
            </div>
          ))}
        </div>

        {/* Contract row */}
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="sh-label">Allocation ID</span>
            <span className="sh-mono" style={{ color: 'var(--text-2)', fontSize: 12 }}>
              {allocationId}
            </span>
            <span className="sh-label">Beneficiary</span>
            <span className="sh-mono" style={{ color: 'var(--text-2)', fontSize: 12 }}>
              {formatAddrShort(beneficiary)}
            </span>
            <span className="sh-label">Vault</span>
            <span className="sh-mono" style={{ color: 'var(--text-2)', fontSize: 12 }}>
              {formatAddrShort(factory)}
            </span>
          </div>
          {lockAddress && (
            <a
              href={explorerForChain(environment.chainId, lockAddress)}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--info)', fontSize: 12 }}
            >
              View allocation contract ↗
            </a>
          )}
        </div>

        {/* Vesting schedule table */}
        {schedule.length > 0 && (
          <div className="sh-card" style={{ marginTop: 32, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
              <div className="sh-label">Unlock schedule</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6 }}>
                {unlockedProgress.toFixed(2)}% unlocked{' '}
                <span style={{ color: 'var(--text-muted)' }}>
                  · {schedule.filter((s) => s.unlocked && s.vested).length} of {schedule.length}{' '}
                  milestones completed
                </span>
              </div>
              {cancelled && (
                <div className="sh-cause" style={{ marginTop: 8 }}>
                  {/* Only the milestones that were never paid are forfeit. Saying
                      "every milestone below" while some are marked Paid would
                      misdescribe tokens that are already in the holder's wallet. */}
                  {schedule.some((s) => s.settled)
                    ? `Every milestone below is forfeited except the ones marked Paid, which are yours and stay in your wallet.`
                    : `Every milestone below is forfeited.`}{' '}
                  They are kept on screen so the schedule that was cancelled is still legible.
                </div>
              )}
              {/* The milestones are the schedule as it now stands, and it has
                  already paid out past this point — so a row marked unlocked is a
                  statement about the schedule, not about anything still to come. */}
              {!cancelled && aheadOfSchedule > BigInt(0) && (
                <div className="sh-cause" style={{ marginTop: 8 }}>
                  The {claimedTokens} SAHARA you have already received covers everything these
                  milestones have released so far, and {aheadOfScheduleTokens} beyond it.
                  {overpaid > BigInt(0)
                    ? ' Nothing further will be released from this allocation.'
                    : ' Claims reopen once the schedule passes what you have already received.'}
                </div>
              )}
              {/* These rows are the schedule, not your wallet balance: a milestone
                  can be released and still be waiting on its transfer. */}
              {awaitingFunding > BigInt(0) && (
                <div className="sh-cause" style={{ marginTop: 8 }}>
                  {awaitingFundingTokens} SAHARA of what these milestones have released is still
                  awaiting transfer into your allocation contract. {availableTokens} is claimable
                  today.
                </div>
              )}
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table className="sh-table">
                <thead>
                  <tr>
                    <th>Milestone</th>
                    <th>Date</th>
                    <th>% of allocation</th>
                    <th className="num">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row, idx) => (
                    <tr key={idx}>
                      <td className="sh-mono" style={{ color: 'var(--text-muted)' }}>
                        {String(idx + 1).padStart(2, '0')}
                      </td>
                      <td>
                        <div>{formatDate(row.date)}</div>
                        {idx === 0 && (
                          <span className="sh-pill sh-pill-warn" style={{ marginTop: 4 }}>Cliff</span>
                        )}
                      </td>
                      <td className="sh-mono" style={{ color: 'var(--text-2)' }}>
                        {row.percentage.toFixed(2)}%
                      </td>
                      <td className="num sh-num">
                        {web3.formatTokenAmount(row.amount)}
                      </td>
                      <td>
                        {/* Paid first: a milestone the holder has already been
                            handed is neither forfeit nor upcoming, whatever has
                            happened to the grant since. */}
                        {row.settled ? (
                          <span
                            className="sh-pill sh-pill-success"
                            title="Already paid — the SAHARA covering this milestone is in your wallet"
                          >
                            Paid
                          </span>
                        ) : row.forfeited ? (
                          <span
                            className="sh-pill sh-pill-danger"
                            title={
                              cancelled
                                ? 'Cancelled — nothing further will be released'
                                : offboardEffective
                                  ? 'Past the off-board date — vesting is frozen below this milestone'
                                  : 'Above what this allocation can now pay — it will never be released'
                            }
                          >
                            {cancelled ? 'Cancelled' : offboardEffective ? 'Terminated' : 'Not payable'}
                          </span>
                        ) : row.unlocked && row.vested ? (
                          <span className="sh-pill sh-pill-success">Unlocked</span>
                        ) : row.unlocked ? (
                          <span
                            className="sh-pill sh-pill-warn"
                            title="Past its unlock date, but not yet vested — claimable once vesting catches up"
                          >
                            Vesting
                          </span>
                        ) : (
                          <span className="sh-pill">Locked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Several grants
// ---------------------------------------------------------------------------

interface GrantSummary {
  lockAddress: Address;
  lock: LockInfo;
  name: string;
  ceiling: bigint;
}

/**
 * Tabs across a wallet's grants. Each is a real link to
 * /position/:lockAddress, so a grant can be bookmarked and shared — the app uses
 * hash routing, so the fragment survives being pasted.
 */
const PositionSwitcher: React.FC<{
  grants: GrantSummary[];
  activeAddress: string | null;
}> = ({ grants, activeAddress }) => {
  const web3 = useWeb3();
  return (
    <div style={{ marginBottom: 28 }}>
      <div className="sh-label">Your grants</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {grants.map((grant, index) => {
          const isActive = activeAddress?.toLowerCase() === grant.lockAddress.toLowerCase();
          return (
            <Link
              key={grant.lockAddress}
              to={`/position/${grant.lockAddress}`}
              className={`sh-btn sh-btn-sm ${isActive ? 'sh-btn-primary' : ''}`}
              title={`${allocationIdFor(grant.lockAddress)} · ${web3.formatTokenAmount(
                grant.lock.availableAmount,
              )} SAHARA claimable`}
            >
              {grant.name || `Grant ${index + 1}`}
              {grant.lock.cancelled && ' · cancelled'}
            </Link>
          );
        })}
        <Link
          to="/"
          className={`sh-btn sh-btn-sm ${activeAddress ? '' : 'sh-btn-primary'}`}
          title="All of this wallet's grants side by side"
        >
          All grants
        </Link>
      </div>
    </div>
  );
};

/**
 * The wallet-level view for somebody holding several grants.
 *
 * It rolls up AMOUNTS only, and that restriction is the point. Two grants can
 * sit on different tracks with different cliff dates and different onboard
 * dates, so there is no single next-unlock date and no single curve that
 * describes them — a merged one would be a drawing of a schedule nobody has.
 * Dates and progress stay inside each position.
 */
const GrantsOverview: React.FC<{
  grants: GrantSummary[];
  switcher: React.ReactNode;
}> = ({ grants, switcher }) => {
  const web3 = useWeb3();
  const now = useNow();

  const totals = grants.reduce(
    (acc, grant) => ({
      ceiling: acc.ceiling + grant.ceiling,
      claimed: acc.claimed + grant.lock.unlockedAmount,
      available: acc.available + grant.lock.availableAmount,
      earned: acc.earned + grant.lock.earnedUnclaimed,
    }),
    { ceiling: BigInt(0), claimed: BigInt(0), available: BigInt(0), earned: BigInt(0) },
  );
  const cancelledCount = grants.filter((grant) => grant.lock.cancelled).length;
  const available = formatTokenWhole(web3.formatTokenAmount(totals.available));

  return (
    <div className="sh-main">
      <div className="sh-main-inner">
        {switcher}

        <div style={{ marginBottom: 40 }}>
          <div className="sh-label">Allocation · {grants.length} grants</div>
          <h1 className="sh-h1">
            Your{' '}
            <span className="sh-serif" style={{ fontStyle: 'italic', color: 'var(--text-2)' }}>
              SAHARA
            </span>{' '}
            allocations
          </h1>
          <p className="sh-sub" style={{ marginTop: 14 }}>
            This wallet holds {grants.length} separate grants. Each has its own schedule, its own
            unlock track and its own claim, so the figures below add amounts only — open a grant
            for its dates, its curve and its claim button.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 1,
            background: 'var(--border)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
            marginBottom: 32,
          }}
        >
          <div style={{ background: 'var(--surface-1)', padding: '32px 36px' }}>
            <div className="sh-label">Claimable now, across all grants</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 18, flexWrap: 'wrap' }}>
              <span
                className="sh-hero-num"
                style={{ color: totals.available > BigInt(0) ? 'var(--accent)' : 'var(--text)' }}
              >
                {available.whole}
              </span>
              <span className="sh-num" style={{ fontSize: 36, color: 'var(--text-muted)', letterSpacing: '-0.04em' }}>
                .{available.frac}
              </span>
              <span className="sh-hero-unit">SAHARA</span>
            </div>
            {/* Deliberately no "claim all" button. There is no batch claim on
                chain, so one would be N wallet signatures with a partial-failure
                story to explain; each grant is claimed from its own page. */}
            <div className="sh-cause" style={{ marginTop: 20 }}>
              Claimed per grant · each claim is its own onchain transaction
            </div>
          </div>
          <div
            style={{
              background: 'var(--surface-1)',
              padding: '32px 36px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 20,
              alignContent: 'center',
            }}
          >
            <div>
              <div className="sh-label">Total allocation</div>
              <div className="sh-num" style={{ fontSize: 20, marginTop: 8 }}>
                {web3.formatTokenAmount(totals.ceiling)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                {cancelledCount > 0
                  ? `Excludes ${cancelledCount} cancelled grant${cancelledCount === 1 ? '' : 's'}`
                  : 'Across every grant'}
              </div>
            </div>
            <div>
              <div className="sh-label">Claimed to date</div>
              <div className="sh-num" style={{ fontSize: 20, marginTop: 8 }}>
                {web3.formatTokenAmount(totals.claimed)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Already in your wallet
              </div>
            </div>
            <div>
              <div className="sh-label">Earned, unclaimed</div>
              <div className="sh-num" style={{ fontSize: 20, marginTop: 8 }}>
                {web3.formatTokenAmount(totals.earned)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Owed whether or not it has been transferred in
              </div>
            </div>
            <div>
              <div className="sh-label">Next unlock</div>
              <div style={{ fontSize: 13, marginTop: 10, color: 'var(--text-2)' }}>
                Per grant
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                These grants run on different tracks and different dates; there is no single one
              </div>
            </div>
          </div>
        </div>

        <div className="sh-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <div className="sh-label">Grants</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6 }}>
              Open one to see its schedule and claim from it.
            </div>
          </div>
          {grants.map((grant, index) => {
            const { lock } = grant;
            const offboardSec = Number(lock.offboardTime);
            const offboardEffective = offboardSec > 0 && offboardSec * 1000 <= now;
            return (
              <div
                key={grant.lockAddress}
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <div style={{ minWidth: 260 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="sh-mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {allocationIdFor(grant.lockAddress)}
                    </span>
                    <span className="sh-pill">{categoryLabel(lock.category)} track</span>
                    {lock.cancelled && <span className="sh-pill sh-pill-danger">Cancelled</span>}
                    {offboardEffective && !lock.cancelled && (
                      <span className="sh-pill sh-pill-danger">Vesting frozen</span>
                    )}
                  </div>
                  {grant.name && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span className="sh-cause">Operator label</span>
                      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>“{grant.name}”</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
                  <div>
                    <div className="sh-label">Allocation</div>
                    <div className="sh-num" style={{ fontSize: 15, marginTop: 6 }}>
                      {web3.formatTokenAmount(grant.ceiling)}
                    </div>
                  </div>
                  <div>
                    <div className="sh-label">Claimable now</div>
                    <div
                      className="sh-num"
                      style={{
                        fontSize: 15,
                        marginTop: 6,
                        color: lock.availableAmount > BigInt(0) ? 'var(--accent)' : undefined,
                      }}
                    >
                      {web3.formatTokenAmount(lock.availableAmount)}
                    </div>
                  </div>
                  <Link
                    to={`/position/${grant.lockAddress}`}
                    className="sh-btn sh-btn-sm"
                    aria-label={`Open ${grant.name || `grant ${index + 1}`}`}
                  >
                    Open
                    <Icon d={ICONS.chevron} size={12} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

export const AllocationScreen: React.FC = () => {
  const tokenLock = useTokenLock();
  // App.tsx renders this element for BOTH "/" and "/position/:lockAddress", at
  // the same depth so React reconciles rather than remounts, and it has already
  // guaranteed that a present param is a grant this wallet holds. So: param
  // present, render that grant; absent with exactly one grant, render it
  // directly; absent with several, render the list.
  const { lockAddress: routeParam } = useParams<{ lockAddress: string }>();
  const now = useNow();

  const grants: GrantSummary[] = useMemo(() => {
    const loaded = tokenLock.lockAddresses
      .map((lockAddress, index) => {
        const lock = tokenLock.locks[index];
        if (!lock) return null;
        return {
          lockAddress,
          lock,
          name: tokenLock.lockNames[index] ?? '',
          ceiling: payableCeiling(lock, now),
        };
      })
      .filter((grant): grant is GrantSummary => grant !== null);
    const grantCount = loaded.length;
    return loaded.map((grant) => ({
      ...grant,
      name: beneficiaryGrantLabel(grant.name, grantCount),
    }));
  }, [tokenLock.lockAddresses, tokenLock.locks, tokenLock.lockNames, now]);

  const activeIndex = useMemo(() => {
    if (routeParam) {
      return grants.findIndex(
        (grant) => grant.lockAddress.toLowerCase() === routeParam.toLowerCase(),
      );
    }
    return grants.length === 1 ? 0 : -1;
  }, [routeParam, grants]);

  // The addresses land before the per-grant states do, so there is a frame where
  // this wallet is known to hold grants but none of them can be rendered yet.
  if (grants.length === 0) {
    return (
      <div
        className="sh-main"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}
      >
        <Spinner label="Reading allocation" />
      </div>
    );
  }

  const switcher =
    grants.length > 1 ? (
      <PositionSwitcher
        grants={grants}
        activeAddress={activeIndex === -1 ? null : grants[activeIndex]!.lockAddress}
      />
    ) : null;

  const active = activeIndex === -1 ? null : grants[activeIndex]!;
  if (!active) {
    return <GrantsOverview grants={grants} switcher={switcher} />;
  }

  return (
    <PositionView
      tokenLock={tokenLock}
      lock={active.lock}
      lockAddress={active.lockAddress}
      name={active.name}
      switcher={switcher}
    />
  );
};
